import { getStoredGclid, getStoredFbclid } from "./track";
import { getFirstTouch, getCompanySiteVisitorKey } from "./analytics";

const PORTAL_ONBOARDING_BASE = "https://portal.stylon.co/onboarding";
const PORTAL_LOGIN = "https://portal.stylon.co/login";

// ── Hero phone-number storage ────────────────────────────────────────────────
// When a visitor types a phone in the hero form, we persist it to localStorage
// so that *any* "Get Started" CTA they click later (navbar, BizCategories, FAQ,
// pricing, footer — anywhere on the site) carries the phone forward to the
// portal onboarding wizard as `?phone=...`. The portal's SimpleOnboarding
// reads that URL param and pre-fills its phone step (initialPhoneRef).
//
// Without this, the phone was forwarded only by the contact-me popup, which
// fires after the user explicitly clicks the "send" button — meaning users who
// type their phone and then scroll up to click Start Now lose the pre-fill.

const HERO_PHONE_KEY = "stylon_hero_phone";

/** Persist the hero phone (or clear if empty). Called from Header.js on every input change. */
export function setStoredHeroPhone(phone) {
  try {
    const trimmed = String(phone || "").trim();
    if (trimmed) localStorage.setItem(HERO_PHONE_KEY, trimmed);
    else localStorage.removeItem(HERO_PHONE_KEY);
  } catch (_) {}
}

/** Read the persisted hero phone, or empty string. */
export function getStoredHeroPhone() {
  try {
    return localStorage.getItem(HERO_PHONE_KEY) || "";
  } catch (_) {
    return "";
  }
}

/**
 * Dedupe key for "we already sent this hero phone to /potential-
 * customers". The auto-save useEffect resets on every mount (the
 * `savedRef` was component-instance scoped), so every navigation
 * back to the company site was firing a fresh duplicate save
 * because the phone was already restored from localStorage on mount.
 *
 * Persisting the last-saved phone here breaks that loop: the auto-
 * save skips when current phone === last-saved phone. If the user
 * changes phone in the field, the new value will mismatch and a
 * single fresh save fires.
 *
 * The stored value is normalised (digits only) so trivial
 * differences like " ", dashes, leading +, or Arabic-script
 * variants don't cause spurious re-sends of the same number.
 */
const HERO_LEAD_SAVED_PHONE_KEY = "stylon_hero_lead_saved_phone";

function phoneFingerprint(phone) {
  if (!phone) return "";
  return String(phone)
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/\D/g, "");
}

/** True when this exact phone (script-agnostic fingerprint) has already been saved. */
export function isHeroLeadAlreadySaved(phone) {
  try {
    const fp = phoneFingerprint(phone);
    if (!fp) return false;
    return localStorage.getItem(HERO_LEAD_SAVED_PHONE_KEY) === fp;
  } catch (_) {
    return false;
  }
}

/** Mark this phone as already saved so we don't re-fire on next mount. */
export function markHeroLeadSaved(phone) {
  try {
    const fp = phoneFingerprint(phone);
    if (fp) localStorage.setItem(HERO_LEAD_SAVED_PHONE_KEY, fp);
  } catch (_) {}
}

/** Returns true when the stored phone has enough digits to plausibly be real. */
function isPhonePlausiblyComplete(raw) {
  return String(raw || "").replace(/\D/g, "").length >= 9;
}

/**
 * "Paid visitor" = arrived on the site with a UTM tag or Google Ads
 * click ID, either right now (current URL) or earlier this session
 * (first-touch attribution stored by analytics.js).
 *
 * Used to gate flows that would otherwise present an unnecessary
 * choice — e.g. the hero phone form's success modal — for visitors
 * who clicked an ad and clearly want to convert, not browse.
 */
export function isPaidVisitor() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("utm_source") ||
      params.get("gclid") ||
      params.get("gbraid") ||
      params.get("wbraid")
    ) {
      return true;
    }
    // Fall back to the per-tab attribution snapshot — the visitor may
    // have landed on a UTM-tagged URL and then navigated away from it.
    const raw = sessionStorage.getItem("stylon_company_site_attribution");
    if (raw) {
      const a = JSON.parse(raw);
      if (a && (a.utm_source || a.gclid)) return true;
    }
  } catch (_) {}
  return false;
}

// ── The latest Social touch ─────────────────────────────────────────────────
// A link in one of Stylon's own Social posts
// (`?utm_source=social&utm_medium=organic_social&utm_campaign=sp_<id>&ref=social&sp=<id>`,
// stylon-server social/growth/social-link.ts) names the post a sign-up came from.
// A visitor who lands from it and reads on before signing up is still that
// post's sign-up — but only while it is the LATEST real touch: a later landing
// with a PAID click id or another utm_source replaces it (the ad keeps its
// sign-up). fbclid alone is not a paid touch: Meta adds it to organic taps too
// (and Stylon's own Meta ads always carry utm tags, so they replace it by
// their utm_source). Moving between the site's own pages keeps it. Every other
// channel is forwarded from the page's own address, as it always was.

const SOCIAL_TOUCH_KEY = "stylon_social_touch";
/**
 * Click ids only a PAID click carries. Never fbclid: Meta adds it to every
 * outbound tap, organic ones included — a paid Meta ad is told by its utm tags.
 */
const PAID_CLICK_IDS = ["gclid", "gbraid", "wbraid", "msclkid", "ttclid", "twclid", "li_fat_id", "dclid", "yclid", "epik"];
/** Where track.js remembers a paid click id's landing time (ms), across tabs (gclid, gbraid and wbraid). Never fbclid's. */
const PAID_CLICK_TIMES = ["stylon_gclid_ts"];

/**
 * What a page load in this tab landed on, called by App on mount: a Social link is
 * kept as the tab's Social touch; a paid click id or another utm_source takes it
 * away; an address with neither (the site's own pages, an organic fbclid tap)
 * changes nothing.
 */
export function noteSocialLanding(search) {
  try {
    const q = new URLSearchParams(search || "");
    const source = q.get("utm_source");
    if (source === "social") {
      sessionStorage.setItem(SOCIAL_TOUCH_KEY, JSON.stringify({
        utm_source: "social",
        utm_medium: q.get("utm_medium") || undefined,
        utm_campaign: q.get("utm_campaign") || undefined,
        ref: q.get("ref") || undefined,
        sp: q.get("sp") || undefined,
        at: Date.now(),
      }));
    } else if (source || PAID_CLICK_IDS.some((k) => q.get(k))) {
      sessionStorage.removeItem(SOCIAL_TOUCH_KEY);
    }
  } catch (_) {}
}

/** The tab's Social touch, unless a paid click remembered after it (in any tab) is newer. */
function socialTouch() {
  try {
    const raw = sessionStorage.getItem(SOCIAL_TOUCH_KEY);
    const t = raw ? JSON.parse(raw) : null;
    if (!t || t.utm_source !== "social") return null;
    const at = Number(t.at) || 0;
    const newerClick = PAID_CLICK_TIMES.some((k) => (parseInt(localStorage.getItem(k) || "0", 10) || 0) > at);
    return newerClick ? null : t;
  } catch (_) {
    return null;
  }
}

/**
 * Build the onboarding URL, forwarding any UTM params from the current page plus
 * the GCLID (Google Ads click ID) so attribution survives the domain hop from
 * stylon.co → portal.stylon.co.
 *
 * GCLID forwarding strategy:
 *   1. The Google Tag's cross-domain measurement config (stylon.co + portal.stylon.co)
 *      causes GTM's Conversion Linker to auto-append `_gl=...` to outbound links —
 *      that's the canonical Google mechanism.
 *   2. As a redundancy (cookie blockers, Safari ITP edge cases), we ALSO forward
 *      the GCLID as a plain `?gclid=...` query param, captured from the current
 *      URL or restored from localStorage. The portal's track.js does the same
 *      capture on its end. Either route gets attribution through.
 *
 * Optional `opts` lets pricing-page CTAs pre-select a plan: visitors land
 * on /onboarding?plan=<id>&period=MONTHLY|YEARLY and get redirected to
 * /membership/checkout/<id> at the end of the wizard.
 *
 * @param {string} section
 * @param {{ planId?: string, period?: 'MONTHLY' | 'YEARLY' }} [opts]
 */
export function getOnboardingUrl(section = "cta", opts = {}) {
  const src = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const dest = new URLSearchParams();
  // A page without tags or a paid click id of its own, in a tab whose latest touch was a Stylon Social post (an
  // organic fbclid on this page leaves it Social: it travels beside the tags, and the server lets the utm win).
  const social = src.get("utm_source") || PAID_CLICK_IDS.some((k) => src.get(k)) ? null : socialTouch();
  const tag = (k) => src.get(k) || (social && social[k]) || undefined;

  // Forward attribution present-only — never relabel untagged traffic as
  // company_site/website. That destructive default was silently overwriting
  // real "no UTM" signal with a fake one at the domain-hop boundary.
  const passIf = (k, v) => { if (v) dest.set(k, v); };
  passIf("utm_source", tag("utm_source")); // NO default
  passIf("utm_medium", tag("utm_medium")); // NO default
  passIf("utm_campaign", tag("utm_campaign"));
  dest.set("utm_content", section); // CTA location, always
  passIf("utm_term", src.get("utm_term"));

  // Forward Google Ads click ID for attribution. Prefer the live URL param if
  // present (covers the literal first-click case), otherwise the stored value
  // (covers users who already landed once and are now clicking a CTA later).
  const liveGclid = src.get("gclid") || src.get("gbraid") || src.get("wbraid");
  passIf("gclid", liveGclid || getStoredGclid());
  // Same pattern for Meta's click ID.
  passIf("fbclid", src.get("fbclid") || getStoredFbclid());

  // First-touch referrer/landing-path + persistent visitor key, so the portal
  // can stitch this session to the one that first landed on the company site.
  const ft = getFirstTouch();
  // A Social post's own `ref=social` and `sp` (the post) travel as they came.
  if (tag("ref") === "social") dest.set("ref", "social");
  else passIf("ref", src.get("utm_source") || social ? undefined : ft.referrer); // referrer only useful when no explicit source
  passIf("sp", tag("sp"));
  passIf("lp", ft.landingPath);
  passIf("fid", getCompanySiteVisitorKey());

  // Forward the hero phone if the visitor typed a plausibly-complete one earlier
  // (any "Get Started" CTA — not just the contact-me popup — pre-fills onboarding).
  const heroPhone = getStoredHeroPhone();
  if (heroPhone && isPhonePlausiblyComplete(heroPhone)) {
    dest.set("phone", heroPhone);
  }

  if (opts.planId) {
    dest.set("plan", opts.planId);
    // Only meaningful when a plan is also set — period alone can't drive
    // the post-onboarding checkout redirect.
    if (opts.period === "MONTHLY" || opts.period === "YEARLY") {
      dest.set("period", opts.period);
    }
  }

  // Remove empty values to keep URL clean
  for (const [k, v] of [...dest.entries()]) {
    if (!v) dest.delete(k);
  }

  return `${PORTAL_ONBOARDING_BASE}?${dest.toString()}`;
}

export { PORTAL_LOGIN };
